import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { unByKey } from 'ol/Observable';
import { Select } from 'ol/interaction';

import { LuLayoutGrid } from 'react-icons/lu';

import { useProject } from '@contexts/Project.context';
import { getHexagonFeatures } from '@utils/api';
import {
	makeHexagonLayer,
	hexagonStyle,
	makeHexagonFeatures,
	DEFAULT_HEXAGON_OPACITY,
} from '@utils/map/layer/hexagon';
import { enrichHexagonData } from '@utils/map/hexagonTransform';
import { addHexagonInteraction } from '../sidebars/HexagonInfo';
import LayerSettings from '../sidebars/sidebarElements/LayerSettings';
import { HexagonSummaryPreview } from '../sidebars/summary/HexagonSummarySidebar';
import {
	ToggleSlideButton,
	ToggleSlideContent,
} from '@components/toggleSlideContent/ToggleSlideContent';
import { getLayerByCustomId } from '@utils/map/helpers';
import FilterHexagon from '../modifyLayers/FilterHexagon';

/**
 * Calculate a zoom-based opacity multiplier for the hexagon layer.
 * Higher zoom = more transparent, lower zoom = more opaque.
 * Tuned for typical drone project zoom range (15–22).
 * @param {number} zoom - Current map zoom level
 * @param {number} minZoom - Zoom level at which opacity is at maximum (default: 15)
 * @param {number} maxZoom - Zoom level at which opacity is at minimum (default: 22)
 * @param {number} minOpacity - Minimum opacity multiplier (default: 0)
 * @param {number} maxOpacity - Maximum opacity multiplier (default: DEFAULT_HEXAGON_OPACITY)
 * @returns {number} - Opacity multiplier between minOpacity and maxOpacity
 */
const getZoomBasedOpacity = (
	zoom,
	minZoom = 15,
	maxZoom = 22,
	minOpacity = 0,
	maxOpacity = DEFAULT_HEXAGON_OPACITY
) => {
	// `view.getZoom()` can return `undefined` while the view's resolution
	// doesn't correspond exactly to an integer zoom level (e.g. mid-animation
	// or fractional zoom from scroll/pinch). Fall back to the resting opacity
	// rather than letting `NaN` flow into the computed style color.
	if (zoom == null || Number.isNaN(zoom)) return maxOpacity;
	if (zoom <= minZoom) return maxOpacity;
	if (zoom >= maxZoom) return minOpacity;
	const ratio = (zoom - minZoom) / (maxZoom - minZoom);
	return maxOpacity - ratio * (maxOpacity - minOpacity);
};

const layerId = 'hexagonLayer';
const HexagonLayer = ({
	setModifyHexagonMode,
	setHexagonColorFilterActive,
	modifyHexagonsSidebarId,
}) => {
	const {
		mapObject,
		hexagonLayer,
		project,
		pickedTask,
		hexagonSize,
		hexagonColor,
		detectionsFilter,
		dispatch,
	} = useProject();
	const { uuid: projectUuid } = project ?? {};
	const { model_uuid: modelUuid } = pickedTask ?? {};

	const [showLayerDetails, setShowLayerDetails] = useState(false);
	const activeFilter = detectionsFilter?.isDrafting
		? detectionsFilter?.draft
		: detectionsFilter?.applied;

	const { data, isLoading } = useQuery({
		queryKey: [
			'fetchHexagons',
			projectUuid,
			modelUuid,
			hexagonSize,
			detectionsFilter,
			activeFilter,
		],
		queryFn: () => getHexagonFeatures(projectUuid, modelUuid, hexagonSize, null, activeFilter),
		select: enrichHexagonData,
		enabled: !!projectUuid && !!modelUuid && !!mapObject,
		refetchOnWindowFocus: false,
		retry: false,
	});

	const hexagonInteractionAdded = useRef(false);

	const { hexagons } = useMemo(() => {
		if (!data?.hexagon_data) {
			return { hexagons: [] };
		}

		let hexagons = [];
		let maxCount = 0;

		Object.keys(data.hexagon_data).forEach(key => {
			const hexagon = { ...data.hexagon_data[key] };
			hexagon.id = key;
			hexagon.metadata = { ...hexagon.metadata };
			hexagon.detections = { ...hexagon.detections };

			if (hexagon.metadata.count > maxCount) {
				maxCount = hexagon.metadata.count;
			}

			hexagons.push(hexagon);
		});

		// Compute heatIndicator and attach detection names.
		// Note: stem enrichment is handled immutably via the query's `select` option.
		hexagons.forEach(hexagon => {
			hexagon.metadata.heatIndicator =
				maxCount > 0 ? (hexagon.metadata.count / maxCount).toFixed(2) : '0.00';
			Object.keys(hexagon.detections).forEach(key => {
				hexagon.detections[key] = { ...hexagon.detections[key], name: key };
			});
		});

		return { hexagons };
	}, [data]);

	const updateHexagonLayer = useCallback(() => {
		if (isLoading) return;

		if (hexagons && hexagons.length > 0) {
			let layer;
			const existingLayer = getLayerByCustomId(mapObject, layerId);
			if (existingLayer) {
				layer = existingLayer;
				const layerSource = layer.getSource();
				layerSource.clear();
				const newFeatures = makeHexagonFeatures(hexagons, hexagonColor);
				layerSource.addFeatures(newFeatures);
			} else {
				layer = makeHexagonLayer({
					currentMap: mapObject,
					features: hexagons,
					hexagonColor,
					customLayerId: layerId,
				});

				addHexagonInteraction(layer, mapObject, dispatch, hexagonInteractionAdded);
			}

			dispatch({ type: 'setHexagonLayer', payload: layer });
			dispatch({ type: 'setHexagonInfo', payload: data });
		} else {
			if (mapObject && hexagonLayer) {
				mapObject.removeLayer(hexagonLayer);

				hexagonLayer.dispose();

				if (hexagonInteractionAdded.current) {
					mapObject.removeInteraction(hexagonInteractionAdded.current);
					hexagonInteractionAdded.current = false;
				}

				dispatch({ type: 'setHexagonLayer', payload: null });
				dispatch({ type: 'setHexagonInfo', payload: null });
				dispatch({ type: 'setSelectedHexagon', payload: null });
			}
		}
	}, [hexagons, detectionsFilter]);

	useEffect(() => {
		updateHexagonLayer();
	}, [updateHexagonLayer]);

	// Apply hexagon fill style (color + zoom-based opacity) in one place.
	// Combined into a single effect so color changes and zoom changes never
	// race to overwrite each other's `setStyle` calls — previously this was
	// split across two effects that relied on React's effect-ordering to
	// apply the zoom opacity last.
	useEffect(() => {
		if (!mapObject || !hexagonLayer) return;

		const view = mapObject.getView();

		const applyStyles = () => {
			const zoomOpacity = getZoomBasedOpacity(view.getZoom());

			// Get currently selected features to avoid overriding their highlight style
			const selectInteractions = mapObject
				.getInteractions()
				.getArray()
				.filter(i => i instanceof Select);
			const selectedFeatures = new Set();
			selectInteractions.forEach(i => {
				i.getFeatures().forEach(f => selectedFeatures.add(f));
			});

			// Apply uniform color + opacity to all hexagons based on zoom only
			hexagonLayer
				?.getSource()
				?.getFeatures()
				?.forEach(feature => {
					if (selectedFeatures.has(feature)) return;
					const style = hexagonStyle(hexagonColor.color, zoomOpacity.toFixed(2));
					feature.setStyle(style);
				});
		};

		// Apply immediately using the current zoom/color
		applyStyles();

		// Re-apply on zoom changes
		const resolutionKey = view.on('change:resolution', applyStyles);

		return () => {
			unByKey(resolutionKey);
		};
	}, [mapObject, hexagonLayer, hexagonColor, hexagons]);

	if (!hexagonLayer) return null;

	return (
		<li>
			<div className="d-flex flex-row justify-content-between align-items-center">
				<LayerSettings
					id="hexagon-layer-settings"
					layer={hexagonLayer}
					label={hexagonLayer.get('name')}
					defaultState={hexagonLayer?.getVisible()}
					onCheck={() => hexagonLayer?.setVisible(true)}
					onUncheck={() => hexagonLayer?.setVisible(false)}
					layerFunctions={[
						{
							title: 'Modify',
							onClick: () => {
								setModifyHexagonMode(true);
							},
						},
						{
							title: 'Color Filter',
							icon: <LuLayoutGrid />,
							onClick: setHexagonColorFilterActive,
						},
					]}
				/>
				<div className="position-relative me-2">
					<div className="position-absolute top-0 start-100 translate-middle">
						<ToggleSlideButton
							constrolsId="hexagon-layer-details"
							isOpen={showLayerDetails}
							setIsOpen={setShowLayerDetails}
						/>
					</div>
				</div>
			</div>

			<ToggleSlideContent isOpen={showLayerDetails} id="hexagon-layer-details">
				<HexagonSummaryPreview />
			</ToggleSlideContent>

			<FilterHexagon sidebarId={modifyHexagonsSidebarId} />
		</li>
	);
};

export default HexagonLayer;
